import { Accordion, AccordionDetails, AccordionSummary, Box, FormControlLabel, Grid, TextField, TextFieldProps, Typography } from '@mui/material';
import { WO2Checkbox, WO2ToggleSwitch } from 'src/common';
import { BaseReportComponentProps, MdaReportTypes, ReportFormat, YearType } from '../../store/types';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { DateRange } from 'src/store';
import { ExpandMore } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import { DateTime } from 'luxon';
import { YearTypeSelector } from '../YearTypeSelector';

export interface InvestmentIncomeTaxProps extends BaseReportComponentProps {
  setInvestmentIncomeTaxSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setDateRangeInvestmentIncomeTaxParameter: ActionCreatorWithPayload<DateRange>;
  setInvestmentIncomeTaxExcludeFxParameter: ActionCreatorWithPayload<boolean, string>;
  setInvestmentIncomeTaxYearTypeParameter: ActionCreatorWithPayload<YearType>;
  setInvestmentIncomeTaxVersionParameter: ActionCreatorWithPayload<boolean, string>;
  expandInvestmentIncomeTax: ActionCreatorWithoutPayload<string>;
}

export const InvestmentIncomeTax = ({
  parameters,
  setInvestmentIncomeTaxSelectedParameter,
  setDateRangeInvestmentIncomeTaxParameter,
  setInvestmentIncomeTaxExcludeFxParameter,
  setInvestmentIncomeTaxYearTypeParameter,
  setInvestmentIncomeTaxVersionParameter,
  expandInvestmentIncomeTax,
  createReportSortRequest,
}: InvestmentIncomeTaxProps) => {
  const handleClickInvestmentIncomeTaxSelect = () => {
    setInvestmentIncomeTaxSelectedParameter(!parameters.selectedReports?.investmentIncomeTaxComponent.selected);
    createReportSortRequest(
      !parameters.selectedReports?.investmentIncomeTaxComponent.selected,
      MdaReportTypes.InvestmentIncomeTax.id,
      MdaReportTypes.InvestmentIncomeTax.displayName
    );
  };

  const updateInvestmentIncomeTaxDateRange = (date: DateTime, yearType: YearType) => {
    if (!date || !date.isValid) return;

    const year = date.year;

    const dateRange: DateRange =
      yearType === YearType.FinancialYear
        ? {
            dateFrom: DateTime.fromObject({ year: year - 1, month: 7, day: 1 }).toISODate(),
            dateTo: DateTime.fromObject({ year, month: 6, day: 30 }).toISODate(),
          }
        : {
            dateFrom: DateTime.fromObject({ year, month: 1, day: 1 }).toISODate(),
            dateTo: DateTime.fromObject({ year, month: 12, day: 31 }).toISODate(),
          };
    setDateRangeInvestmentIncomeTaxParameter(dateRange);
  };

  const handleClickDateForInvestmentIncomeTax = (date: DateTime | null) => {
    if (!date || !date.isValid) return;

    updateInvestmentIncomeTaxDateRange(date, parameters.selectedReports.investmentIncomeTaxComponent.yearType);
  };

  useEffect(() => {
    const { toDate, yearType } = parameters.selectedReports.investmentIncomeTaxComponent;

    if (!toDate) return;

    const date = DateTime.fromISO(toDate);
    if (!date.isValid) return;

    updateInvestmentIncomeTaxDateRange(date, yearType);
  }, [parameters.selectedReports.investmentIncomeTaxComponent.yearType]);

  return (
    <Accordion
      onChange={expandInvestmentIncomeTax}
      expanded={parameters.selectedReports?.investmentIncomeTaxComponent.expanded || false}
      elevation={0}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.investmentIncomeTaxComponent.selected}
              onChangeHandler={handleClickInvestmentIncomeTaxSelect}
            />
          }
          label="Investment Income Tax Component"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              label={parameters.selectedReports.investmentIncomeTaxComponent.yearType.toString()}
              views={['year']}
              value={
                parameters.selectedReports.investmentIncomeTaxComponent.toDate
                  ? DateTime.fromISO(parameters.selectedReports.investmentIncomeTaxComponent.toDate)
                  : null
              }
              onChange={handleClickDateForInvestmentIncomeTax}
              minDate={DateTime.fromObject({ year: 2000 })}
              maxDate={DateTime.fromObject({ year: DateTime.now().year, month: 12, day: 31 })}
              renderInput={(props: TextFieldProps) => <TextField sx={{ minWidth: 194, maxWidth: 194, mr: 1 }} {...props} />}
            />
          </LocalizationProvider>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" gap={2} flexDirection="column">
          <YearTypeSelector
            value={parameters.selectedReports.investmentIncomeTaxComponent.yearType}
            setValue={(value: YearType) => setInvestmentIncomeTaxYearTypeParameter(value)}
          />
          <Grid item container direction="row" alignItems="center">
            <Grid item container xs={6} alignItems="center">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.investmentIncomeTaxComponent.excludeFxGainsLosses}
                onChange={(_, checked: boolean) => {
                  setInvestmentIncomeTaxExcludeFxParameter(checked);
                }}
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Exclude FX Gains/Losses</Typography>
            </Grid>
            {parameters.reportFormat === ReportFormat.Excel && (
              <Grid item container xs={6} alignItems="center">
                <WO2ToggleSwitch
                  checked={parameters.selectedReports.investmentIncomeTaxComponent.version === 'Flattened'}
                  onChange={(_, checked: boolean) => {
                    setInvestmentIncomeTaxVersionParameter(checked);
                  }}
                  color="primary"
                  name="hidingOptions"
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                />
                <Typography variant="h4">Flattened Version</Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
